import React, { useState, useEffect } from 'react';
import { Navbar as BootstrapNavbar, Container, Dropdown, Form, InputGroup, Button, Modal, Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import './Navbar.css';

interface Category {
  categoryId: number;
  categoryName: string;
  categoryNameEn?: string;
  icon?: string;
  color?: string;
  displayOrder: number;
  isActive: boolean;
  itemsCount: number;
}

interface NavbarProps {
  categories?: Category[];
  itemsCount?: number;
  selectedCategoryId?: number | null;
  onCategoryClick?: (categoryId: number | null) => void;
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
}

const Navbar: React.FC<NavbarProps> = ({ 
  categories = [], 
  itemsCount = 0, 
  selectedCategoryId = null,
  onCategoryClick,
  searchTerm: externalSearchTerm = '',
  onSearchChange
}) => {
  const { t } = useTranslation();
  const { language, setLanguage, isRTL } = useLanguage();
  const { currency, setCurrency, availableCurrencies } = useCurrency();
  const { getTotalItems } = useCart();
  const { user, logout, isAuthenticated, login, register } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState(externalSearchTerm);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  
  // Login form state
  const [loginData, setLoginData] = useState({ mobileDigits: '', country: 'SA', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Register form state
  const [registerData, setRegisterData] = useState({
    firstName: '',
    lastName: '',
    userName: '',
    email: '',
    mobileDigits: '',
    country: 'SA',
    password: '',
    confirmPassword: ''
  });
  const [registerError, setRegisterError] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);
  

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Sync internal searchTerm with external prop
  useEffect(() => {
    setSearchTerm(externalSearchTerm);
  }, [externalSearchTerm]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearchChange) {
      onSearchChange(searchTerm);
    }
    // Scroll to products section if on home page
    const productsSection = document.querySelector('.products-section-modern');
    if (productsSection) {
      productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleLoginChange = (e: React.ChangeEvent<any>) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value
    });
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    let formattedMobile = loginData.mobileDigits;
    if (formattedMobile.startsWith('0')) {
      formattedMobile = formattedMobile.substring(1);
    }

    const countryCode = loginData.country === 'SA' ? '+966' : '+249';
    const fullMobile = `${countryCode}${formattedMobile}`;

    try {
      const result = await login(fullMobile, loginData.password);
      if (result && result.success) {
        setShowLoginModal(false);
        setLoginData({ mobileDigits: '', country: 'SA', password: '' });
        navigate('/');
      } else {
        const msg = result?.message || t('loginErrorInvalid');
        setLoginError(msg as string);
        
        // Handle unregistered user
        if (result?.status === 404 || (typeof msg === 'string' && (msg.includes('not registered') || msg.includes('غير مسجل')))) {
          setTimeout(() => {
            setShowLoginModal(false);
            setRegisterData(prev => ({
              ...prev,
              mobileDigits: loginData.mobileDigits,
              country: loginData.country
            }));
            setShowRegisterModal(true);
            setLoginError('');
          }, 1500);
        }
      }
    } catch (err) {
      setLoginError(t('loginErrorGeneric') as string);
    }

    setLoginLoading(false);
  };

  const handleRegisterChange = (e: React.ChangeEvent<any>) => {
    setRegisterData({
      ...registerData,
      [e.target.name]: e.target.value
    });
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterError('');
    setRegisterLoading(true);

    if (registerData.password !== registerData.confirmPassword) {
      setRegisterError(t('registerErrorPasswordMismatch') as string);
      setRegisterLoading(false);
      return;
    }

    let formattedMobile = registerData.mobileDigits;
    if (formattedMobile.startsWith('0')) {
      formattedMobile = formattedMobile.substring(1);
    }
    
    const isSaudi = registerData.country === 'SA';
    const countryCode = isSaudi ? '+966' : '+249';
    const fullMobile = `${countryCode}${formattedMobile}`;

    try {
      const success = await register({
        firstName: registerData.firstName,
        lastName: registerData.lastName,
        userName: fullMobile,
        email: registerData.email,
        mobileNumber: fullMobile,
        password: registerData.password
      });

      if (success) {
        setShowRegisterModal(false);
        setRegisterData({
          firstName: '',
          lastName: '',
          userName: '',
          email: '',
          mobileDigits: '',
          country: 'SA',
          password: '',
          confirmPassword: ''
        });
        navigate('/');
      } else {
        setRegisterError(t('registerErrorGeneric') as string);
      }
    } catch (err) {
      setRegisterError(t('registerErrorGeneric') as string);
    }

    setRegisterLoading(false);
  };

  const getDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    const identifier = user?.userName || user?.firstName;
    if (identifier) {
      // Format mobile number if it starts with +966 or +249
      if (identifier.startsWith('+966')) {
        return '0' + identifier.substring(4);
      }
      if (identifier.startsWith('+249')) {
        return '0' + identifier.substring(4);
      }
      return identifier;
    }
    return t('user') as string;
  };

  return (
    <div className={`main-navbar shadow-sm ${isScrolled ? 'scrolled' : ''}`}>
      <BootstrapNavbar bg="transparent" expand={false} className="custom-navbar">
        <Container>
         
          <div className="navbar-main-section">
            <Link to="/" className="navbar-brand-mic">
              <img 
                src="/LogoT5.png" 
                alt="MIC Green Logo" 
                className="navbar-logo-image animated-fade-in"
                style={{ display: 'block', visibility: 'visible' }}
              />
            </Link>
            
            {/* Search Bar - Main (Hidden when scrolled) */}
            <Form className={`search-bar-mic ${isScrolled ? 'hidden' : ''}`} onSubmit={handleSearch}>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder={t('searchPlaceholder') as string}
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="search-input-mic"
                />
                <Button variant="primary" type="submit" className="search-button-mic">
                  <i className="fas fa-search"></i>
                </Button>
              </InputGroup>
            </Form>

            <div className="navbar-actions">
      
              {/* Language Selector */}
              <Dropdown className="me-2">
                <Dropdown.Toggle variant="link" className="action-link" id="language-dropdown">
                  <i className="fas fa-globe"></i>
                  <span className="ms-1">{language === 'ar' ? t('arabic') : t('english')}</span>
                </Dropdown.Toggle>
                <Dropdown.Menu align="end">
                  <Dropdown.Item 
                    active={language === 'en'} 
                    onClick={() => setLanguage('en')}
                    className="d-flex align-items-center justify-content-between"
                  >
                    <span>{t('english')}</span>
                    {language === 'en' && <i className="fas fa-check text-success"></i>}
                  </Dropdown.Item>
                  <Dropdown.Item 
                    active={language === 'ar'} 
                    onClick={() => setLanguage('ar')}
                    className="d-flex align-items-center justify-content-between"
                  >
                    <span>{t('arabic')}</span>
                    {language === 'ar' && <i className="fas fa-check text-success"></i>}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              
              {/* Currency Selector */}
              <Dropdown className="me-2">
                <Dropdown.Toggle variant="link" className="action-link" id="currency-dropdown">
                  <i className="fas fa-dollar-sign"></i>
                  <span className="ms-1">{currency}</span>
                </Dropdown.Toggle>
                <Dropdown.Menu align="end">
                  <Dropdown.Item 
                    active={currency === 'USD'} 
                    onClick={() => setCurrency('USD')}
                  >
                    <i className="fas fa-check me-2" style={{ visibility: currency === 'USD' ? 'visible' : 'hidden' }}></i>
                    USD - {t('USD_name')}
                  </Dropdown.Item>
                  <Dropdown.Item 
                    active={currency === 'EUR'} 
                    onClick={() => setCurrency('EUR')}
                  >
                    <i className="fas fa-check me-2" style={{ visibility: currency === 'EUR' ? 'visible' : 'hidden' }}></i>
                    EUR - {t('EUR_name')}
                  </Dropdown.Item>
                  <Dropdown.Item 
                    active={currency === 'SAR'} 
                    onClick={() => setCurrency('SAR')}
                  >
                    <i className="fas fa-check me-2" style={{ visibility: currency === 'SAR' ? 'visible' : 'hidden' }}></i>
                    SAR - {t('SAR_name')}
                  </Dropdown.Item>
                  <Dropdown.Item 
                    active={currency === 'AED'} 
                    onClick={() => setCurrency('AED')}
                  >
                    <i className="fas fa-check me-2" style={{ visibility: currency === 'AED' ? 'visible' : 'hidden' }}></i>
                    AED - {t('AED_name')}
                  </Dropdown.Item>
                  <Dropdown.Item 
                    active={currency === 'SDG'} 
                    onClick={() => setCurrency('SDG')}
                  >
                    <i className="fas fa-check me-2" style={{ visibility: currency === 'SDG' ? 'visible' : 'hidden' }}></i>
                    SDG - {t('SDG_name')}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              {isAuthenticated ? (
                <>
                  {/* Home Button */}
                  <Link to="/" className="action-link btn-home-custom" title={t('home') as string}>
                    <i className="fas fa-home"></i>
                    <span className="d-none d-lg-inline">{t('home')}</span>
                  </Link>

                  <div className="cart-link-wrapper">
                    <button 
                      onClick={() => navigate('/product-request')}
                      className="action-link btn-request-custom" 
                      title={t('request') as string}
                    >
                      <i className="fas fa-clipboard-list"></i>
                      <span className="d-none d-lg-inline">{t('importRequest')}</span>
                    </button>
                  </div>

                  <div className="cart-link-wrapper">
                    <button 
                      onClick={() => navigate('/cart')}
                      className="action-link cart-link" 
                      title={t('cart') as string}
                    >
                      <i className="fas fa-shopping-cart"></i>
                      <span className="d-none d-lg-inline">{t('cartText')}</span>
                    </button>
                    {getTotalItems() > 0 && (
                      <span className="cart-badge-mic">{getTotalItems()}</span>
                    )}
                  </div>
             
                  <Dropdown>
                    <Dropdown.Toggle variant="link" className="action-link btn-user-custom">
                      <i className="fas fa-user-circle"></i>
                      <span className="d-none d-lg-inline">
                        {getDisplayName()}
                      </span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="user-dropdown-menu">
                      <Dropdown.Item onClick={() => navigate('/profile')} className="profile-item">
                        <i className="fas fa-user me-2"></i>
                        {t('myProfile') || 'My Profile'}
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item onClick={logout} className="logout-item">
                        <i className="fas fa-sign-out-alt me-2"></i>
                        {t('logout')}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </>
              ) : (
                <>
                  {/* Home Button */}
                  <Link to="/" className="action-link btn-home-custom" title={t('home') as string}>
                    <i className="fas fa-home"></i>
                    <span className="d-none d-lg-inline">{t('home')}</span>
                  </Link>

                  <div className="cart-link-wrapper">
                    <button 
                      onClick={() => navigate('/product-request')}
                      className="action-link btn-request-custom" 
                      title={t('request') as string}
                    >
                      <i className="fas fa-clipboard-list"></i>
                      <span className="d-none d-lg-inline">{t('importRequest')}</span>
                    </button>
                  </div>

                  <div className="cart-link-wrapper">
                    <button 
                      onClick={() => navigate('/cart')}
                      className="action-link cart-link" 
                      title={t('cart') as string}
                    >
                      <i className="fas fa-shopping-cart"></i>
                      <span className="d-none d-lg-inline">{t('cartText')}</span>
                    </button>
                    {getTotalItems() > 0 && (
                      <span className="cart-badge-mic">{getTotalItems()}</span>
                    )}
                  </div>

                  <button 
                    onClick={() => setShowLoginModal(true)}
                    className="action-link btn-login-custom"
                  >
                    <i className="fas fa-user-circle"></i>
                    <span className="d-none d-lg-inline">{t('loginText')}</span>
                  </button>
                </>
              )}
            </div>
          </div>


        </Container>

      </BootstrapNavbar>
      
        {/* Categories and Search Bar - Appears when scrolled */}
        <div className={`categories-navbar-section ${isScrolled ? 'visible' : ''}`}>
          <Container>
            {/* Categories List - Appears when scrolled */}
            {categories.length > 0 && onCategoryClick && (
              <div className={`categories-sidebar ${isScrolled ? 'visible' : ''}`}>
                <div className="categories-list">
                  <button 
                    onClick={() => onCategoryClick(null)}
                    className={`category-item-sidebar ${selectedCategoryId === null ? 'active' : ''}`}
                    title={selectedCategoryId === null ? (t('hideFilters') as string) : (t('showAllProducts') as string)}
                  >
                    <div className="category-item-icon">
                      <i className="fas fa-th"></i>
                    </div>
                    <span className="category-item-name">{t('allCategories')}</span>
                    <span className="category-item-count">{itemsCount}</span>
                    {selectedCategoryId === null && (
                      <i className="fas fa-check category-item-check"></i>
                    )}
                  </button>
                  {categories.map((category) => {
                    const categoryItemsCount = category.itemsCount || 0;
                    const isActive = selectedCategoryId === category.categoryId;
                    const categoryDisplayName = language === 'en' && category.categoryNameEn ? category.categoryNameEn : category.categoryName;
                    
                    return (
                      <button
                        key={category.categoryId}
                        onClick={() => onCategoryClick(category.categoryId)}
                        className={`category-item-sidebar ${isActive ? 'active' : ''}`}
                        title={isActive ? (t('hideFilters') as string) : `${t('showProductsOf')} ${categoryDisplayName}`}
                      >
                        <div 
                          className="category-item-icon" 
                          style={{ 
                            background: category.color 
                              ? `linear-gradient(135deg, ${category.color} 0%, ${category.color}dd 100%)`
                              : 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
                          }}
                        >
                          <i className={category.icon || 'fas fa-folder'}></i>
                        </div>
                        <span className="category-item-name">{categoryDisplayName}</span>
                        <span className="category-item-count">{categoryItemsCount}</span>
                        {isActive && (
                          <i className="fas fa-check category-item-check"></i>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Search Bar - Appears when scrolled in separate row */}
            <div className={`categories-search-bar ${isScrolled ? 'visible' : ''}`}>
              <Form className="search-bar-categories" onSubmit={handleSearch}>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder={t('searchPlaceholder') as string}
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="search-input-categories"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                  <Button variant="primary" type="submit" className="search-button-categories">
                    <i className="fas fa-search"></i>
                  </Button>
                </InputGroup>
              </Form>
            </div>
          </Container>
        </div>

        {/* Cart Modal removed - using separate page at /cart */}

        {/* Login Modal */}
        <Modal 
          show={showLoginModal} 
          onHide={() => {
            setShowLoginModal(false);
            setLoginError('');
            setLoginData({ mobileDigits: '', country: 'SA', password: '' });
          }}
          centered
        >
          <Modal.Header>
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => {
                setShowLoginModal(false);
                setLoginError('');
                setLoginData({ mobileDigits: '', country: 'SA', password: '' });
              }}
              aria-label="Close"
            ></button>
            <Modal.Title>
              <i className="fas fa-sign-in-alt me-2"></i>
              {t('loginForm')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center mb-4">
              <p className="text-muted">{t('welcomeBack')}</p>
            </div>
            
            {loginError && <Alert variant="danger">{loginError}</Alert>}
            
            <Form onSubmit={handleLoginSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <i className="fas fa-user me-2 text-primary"></i>
                  {t('loginIdentifierLabel')}
                </Form.Label>
                <InputGroup dir="ltr">
                  <Form.Select
                    name="country"
                    value={loginData.country}
                    onChange={handleLoginChange}
                    className="py-2 bg-light"
                    style={{ maxWidth: '140px', fontWeight: 'bold' }}
                  >
                    <option value="SA">🇸🇦 +966</option>
                    <option value="SD">🇸🇩 +249</option>
                  </Form.Select>
                  <Form.Control
                    type="tel"
                    name="mobileDigits"
                    value={loginData.mobileDigits}
                    onChange={handleLoginChange}
                    placeholder={t('enterMobileDigits') as string}
                    required
                    className="py-2"
                    inputMode="numeric"
                  />
                </InputGroup>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>
                  <i className="fas fa-lock me-2 text-primary"></i>
                  {t('password')}
                </Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={loginData.password}
                  onChange={handleLoginChange}
                  placeholder={t('passwordPlaceholder') as string}
                  required
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </Form.Group>
              
              <Button 
                variant="primary" 
                type="submit" 
                className="w-100 mb-3"
                disabled={loginLoading}
              >
                {loginLoading ? t('loggingIn') : t('signIn')}
              </Button>
            </Form>
            
            <div className="text-center">
              <p>
                {t('dontHaveAccount')}{' '}
                <button
                  onClick={() => {
                    setShowLoginModal(false);
                    setShowRegisterModal(true);
                  }}
                  style={{ background: 'none', border: 'none', color: '#059669', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  {t('signUp')}
                </button>
              </p>
            </div>
          </Modal.Body>
        </Modal>

        {/* Register Modal */}
        <Modal 
          show={showRegisterModal} 
          onHide={() => {
            setShowRegisterModal(false);
            setRegisterError('');
            setRegisterData({
              firstName: '',
              lastName: '',
              userName: '',
              email: '',
              mobileDigits: '',
              country: 'SA',
              password: '',
              confirmPassword: ''
            });
          }}
          centered
          size="lg"
        >
          <Modal.Header>
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => {
                setShowRegisterModal(false);
                setRegisterError('');
                setRegisterData({
              firstName: '',
              lastName: '',
              userName: '',
              email: '',
              mobileDigits: '',
              country: 'SA',
              password: '',
              confirmPassword: ''
            });
              }}
              aria-label="Close"
            ></button>
            <Modal.Title>
              <i className="fas fa-user-plus me-2"></i>
              {t('registerForm')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center mb-4">
              <p className="text-muted">{t('createAccountMsg')}</p>
            </div>
            
            {registerError && <Alert variant="danger">{registerError}</Alert>}
            
            <Form onSubmit={handleRegisterSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <i className="fas fa-user me-2 text-primary"></i>
                      {t('firstName')}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="firstName"
                      value={registerData.firstName}
                      onChange={handleRegisterChange}
                      placeholder="أدخل الاسم الأول"
                      required
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <i className="fas fa-user me-2 text-primary"></i>
                      {t('lastName')}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="lastName"
                      value={registerData.lastName}
                      onChange={handleRegisterChange}
                      placeholder="أدخل الاسم الأخير"
                      required
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </Form.Group>
                </div>
              </div>
              
              <Form.Group className="mb-3">
                <Form.Label>
                  <i className="fas fa-envelope me-2 text-primary"></i>
                  {t('email')}
                </Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={registerData.email}
                  onChange={handleRegisterChange}
                  placeholder="أدخل بريدك الإلكتروني (اختياري)"
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>
                  <i className="fas fa-mobile-alt me-2 text-primary"></i>
                  رقم الجوال
                </Form.Label>
                <InputGroup dir="ltr">
                  <Form.Select
                    name="country"
                    value={registerData.country}
                    onChange={handleRegisterChange}
                    className="py-2 bg-light"
                    style={{ maxWidth: '140px', fontWeight: 'bold' }}
                  >
                    <option value="SA">🇸🇦 +966</option>
                    <option value="SD">🇸🇩 +249</option>
                  </Form.Select>
                  <Form.Control
                    type="tel"
                    name="mobileDigits"
                    value={registerData.mobileDigits}
                    onChange={handleRegisterChange}
                    placeholder={t('enterMobileDigits') as string}
                    required
                    className="py-2"
                    inputMode="numeric"
                  />
                </InputGroup>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>
                  <i className="fas fa-lock me-2 text-primary"></i>
                  {t('password')}
                </Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={registerData.password}
                  onChange={handleRegisterChange}
                  placeholder="أدخل كلمة المرور (6 أحرف على الأقل)"
                  required
                  minLength={6}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>
                  <i className="fas fa-lock me-2 text-primary"></i>
                  {t('confirmPassword')}
                </Form.Label>
                <Form.Control
                  type="password"
                  name="confirmPassword"
                  value={registerData.confirmPassword}
                  onChange={handleRegisterChange}
                  placeholder="أعد إدخال كلمة المرور"
                  required
                  minLength={6}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </Form.Group>
              
              <Button 
                variant="primary" 
                type="submit" 
                className="w-100 mb-3"
                disabled={registerLoading}
              >
                {registerLoading ? 'جاري إنشاء الحساب...' : t('submit')}
              </Button>
            </Form>
            
            <div className="text-center">
              <p>
                {t('alreadyHaveAccount')}{' '}
                <button
                  onClick={() => {
                    setShowRegisterModal(false);
                    setShowLoginModal(true);
                  }}
                  style={{ background: 'none', border: 'none', color: '#059669', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  {t('signIn')}
                </button>
              </p>
            </div>
          </Modal.Body>
        </Modal>

        {/* Product Request Modal removed - using separate page at /product-request */}
    </div>
  );
};

export default Navbar;
